import { useState } from 'react';
import useTableFiltering from 'shared/components/table/hooks/table-filtering';

import { ReadIntercomIpRoomUnitsSystemResponse } from 'features/systems/ip-unit/domain/models/read-intercom-ip-room-units-response';
import { useReadIntercomIpRoomUnits } from 'features/systems/ip-unit/domain/queries/system-queries';

type Props = {
  gatewayId: string | undefined;
  unavailableSystemIds: string[];
  onClose: () => void;
  onSystemSelected: (system: ReadIntercomIpRoomUnitsSystemResponse) => void;
};

const useIPUnitRoomSystems = ({ gatewayId, unavailableSystemIds, onClose, onSystemSelected }: Props) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedSystem, setSelectedSystem] = useState<ReadIntercomIpRoomUnitsSystemResponse>();
  const {
    currentPagination: [pagination],
    currentPage,
    currentRowsPerPage,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
    resetPaging,
  } = useTableFiltering<ReadIntercomIpRoomUnitsSystemResponse>();
  const { data, isLoading } = useReadIntercomIpRoomUnits(gatewayId, searchQuery, pagination);

  const handleOnClose = (): void => {
    setSelectedSystem(undefined);
    onClose();
  };

  const handleOnSearchValueChanged = (value: string): void => {
    setSelectedSystem(undefined);
    setSearchQuery(value);
    resetPaging();
  };

  const clearSearch = (): void => {
    handleOnSearchValueChanged('');
  };

  const handleOnSelect = (): void => {
    if (!selectedSystem) {
      return;
    }

    onSystemSelected(selectedSystem);
    handleOnClose();
  };

  return {
    systems: data?.systems.filter((system) => !unavailableSystemIds.includes(system.systemId)) ?? [],
    totalSystems: data?.total ?? 0,
    isLoading,
    searchQuery,
    selectedSystem,
    currentPage,
    currentRowsPerPage,
    handleOnClose,
    handleOnSearchValueChanged,
    clearSearch,
    handleOnSelect,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
    setSelectedSystem,
  };
};

export default useIPUnitRoomSystems;
